// src/components/drillholes/data/CollarsUploadDialog.tsx

import { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Upload, FileSpreadsheet, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { utilsExcelSheetsUrl, utilsExcelPreviewUrl, utilsCsvPreviewUrl } from '@/api/urls';

// ── Types ────────────────────────────────────────────────────────────────────

export type CollarsUploadDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (file: File, params: Record<string, string | number>) => void;
};

const COLLAR_FIELDS = [
  { key: 'id_col', label: 'Hole ID',       required: true },
  { key: 'x_col',  label: 'X (Easting)',  required: true },
  { key: 'y_col',  label: 'Y (Northing)', required: true },
  { key: 'z_col',  label: 'Z (Elevation)',required: true },
];

type Step = 'file' | 'sheet' | 'columns' | 'confirm';

const STEP_LABELS: Record<Step, string> = {
  file:    'Select file',
  sheet:   'Select sheet',
  columns: 'Match columns',
  confirm: 'Confirm',
};

// ── Stepper ──────────────────────────────────────────────────────────────────

function Stepper({ steps, current, allSteps }: { steps: Step[]; current: Step; allSteps: Step[] }) {
  const currentIdx = allSteps.indexOf(current);
  return (
    <div className="flex items-center px-6 py-4 border-b border-border pr-12 flex-wrap gap-y-2">
      {steps.map((step) => {
        const i      = allSteps.indexOf(step);
        const done   = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={step} className={cn("flex items-center flex-1 last:flex-none", !active && !done && "hidden sm:flex")}>
            <div className={cn(
              'flex items-center gap-2 text-xs',
              done   && 'text-green-600 dark:text-green-500',
              active && 'text-foreground font-medium',
              !done && !active && 'text-muted-foreground',
            )}>
              <div className={cn(
                'w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-medium flex-shrink-0',
                done   && 'bg-green-50 border-green-300 text-green-600 dark:bg-green-950 dark:border-green-700 dark:text-green-500',
                active && 'bg-foreground border-foreground text-background',
                !done && !active && 'border-border text-muted-foreground',
              )}>
                {done ? '✓' : i + 1}
              </div>
              <span className={cn(!active && 'hidden sm:inline')}>{STEP_LABELS[step]}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={cn(
                "h-px bg-border mx-3",
                (!active && !done) ? "hidden sm:flex-1 sm:block" : "w-3 sm:flex-1"
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function CollarsUploadDialog({ open, onOpenChange, onConfirm }: CollarsUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // file
  const [file,       setFile]       = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isExcel = file ? file.name.endsWith('.xlsx') || file.name.endsWith('.xls') : false;

  // sheet
  const [sheets,         setSheets]         = useState<string[]>([]);
  const [selectedSheet,  setSelectedSheet]  = useState('');
  const [loadingSheets,  setLoadingSheets]  = useState(false);

  // columns
  const [columns,        setColumns]        = useState<string[]>([]);
  const [totalRows,      setTotalRows]      = useState(0);
  const [previewRows,    setPreviewRows]    = useState<string[][]>([]);
  const [mapping,        setMapping]        = useState<Record<string, string>>({});
  const [minRow,         setMinRow]         = useState('2');
  const [maxRow,         setMaxRow]         = useState('');
  const [loadingCols,    setLoadingCols]    = useState(false);

  const [step,  setStep]  = useState<Step>('file');
  const [error, setError] = useState<string | null>(null);

  const steps: Step[] = isExcel
    ? ['file', 'sheet', 'columns', 'confirm']
    : ['file', 'columns', 'confirm'];

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleFilePick = (picked: File) => {
    setFile(picked);
    setError(null);
    setSheets([]);
    setSelectedSheet('');
    setColumns([]);
    setPreviewRows([]);
    setMapping({});
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFilePick(f);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFilePick(f);
  };

  const goToSheet = async () => {
    if (!file) return;
    setLoadingSheets(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(utilsExcelSheetsUrl, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSheets(data.sheets);
      setSelectedSheet(data.sheets[0] ?? '');
      setStep('sheet');
    } catch (err: any) {
      setError(err.message || 'Failed to read sheets');
    } finally {
      setLoadingSheets(false);
    }
  };

  const goToColumns = async () => {
    if (!file) return;
    setLoadingCols(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      let res;
      if (isExcel) {
        const params = new URLSearchParams({ sheetname: selectedSheet });
        res = await fetch(`${utilsExcelPreviewUrl}?${params}`, { method: 'POST', credentials: 'include', body: fd });
      } else {
        res = await fetch(utilsCsvPreviewUrl, { method: 'POST', credentials: 'include', body: fd });
      }
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setColumns(data.columns);
      setTotalRows(data.total_rows);
      setPreviewRows(data.preview_rows ?? []);
      setMapping({});
      setStep('columns');
    } catch (err: any) {
      setError(err.message || 'Failed to read columns');
    } finally {
      setLoadingCols(false);
    }
  };

  const handleNext = async () => {
    if (step === 'file')    { isExcel ? await goToSheet() : await goToColumns(); return; }
    if (step === 'sheet')   { await goToColumns(); return; }
    if (step === 'columns') { setStep('confirm'); return; }
  };

  const handleBack = () => {
    if (step === 'sheet')   { setStep('file');    return; }
    if (step === 'columns') { setStep(isExcel ? 'sheet' : 'file'); return; }
    if (step === 'confirm') { setStep('columns'); return; }
  };

  const handleConfirm = () => {
    if (!file) return;
    // mapping is { col_letter: field_key } — flip to { field_key: col_letter }
    const flipped: Record<string, string> = {};
    Object.entries(mapping).forEach(([col, key]) => { flipped[key] = col; });

    const params: Record<string, string | number> = {
      sheetname: selectedSheet,
      id_col:    flipped['id_col'],
      x_col:     flipped['x_col'],
      y_col:     flipped['y_col'],
      z_col:     flipped['z_col'],
      min_row:   parseInt(minRow) || 2,
    };
    if (maxRow) params.max_row = parseInt(maxRow);
    onConfirm(file, params);
    handleClose();
  };

  const handleClose = () => {
    setFile(null); setSheets([]); setSelectedSheet('');
    setColumns([]); setTotalRows(0); setPreviewRows([]); setMapping({});
    setMinRow('2'); setMaxRow(''); setError(null);
    setStep('file');
    onOpenChange(false);
  };

  const allRequiredMapped = COLLAR_FIELDS
    .filter((f) => f.required)
    .every((f) => Object.values(mapping).includes(f.key));

  // ── Step: file ─────────────────────────────────────────────────────────────

  const StepFile = (
    <div className="flex flex-col gap-4 p-6">
      {!file ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'flex flex-col items-center gap-4 rounded-xl border-2 border-dashed px-10 py-12 cursor-pointer transition-colors',
            isDragging
              ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/20'
              : 'border-border hover:border-muted-foreground/40 hover:bg-muted/30'
          )}
        >
          <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center">
            <Upload size={20} className="text-muted-foreground" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-foreground">Drag and drop your file here</p>
            <p className="text-xs text-muted-foreground mt-1">or click to browse · XLS, XLSX, CSV</p>
          </div>
          <Button size="sm" variant="outline" tabIndex={-1}>Choose file</Button>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
          <div className="w-8 h-8 rounded-md bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center flex-shrink-0">
            <FileSpreadsheet size={16} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isExcel ? 'Excel workbook' : 'CSV file'} · {(file.size / 1024).toFixed(0)} KB
            </p>
          </div>
          <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground p-1 rounded">
            <X size={14} />
          </button>
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileInput} />
    </div>
  );

  // ── Step: sheet ────────────────────────────────────────────────────────────

  const StepSheet = (
    <div className="flex flex-col gap-3 p-6">
      <p className="text-xs text-muted-foreground">Choose which sheet contains your collar data.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {sheets.map((s) => (
          <button
            key={s}
            onClick={() => setSelectedSheet(s)}
            className={cn(
              'text-left px-3 py-2.5 rounded-lg border text-sm transition-colors break-words whitespace-normal h-auto',
              selectedSheet === s
                ? 'border-[2px] border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                : 'border-border text-muted-foreground hover:border-muted-foreground/50'
            )}
          >
            {s}
          </button>
        ))}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );

  // ── Step: columns ──────────────────────────────────────────────────────────

  const StepColumns = (
    <div className="flex flex-col gap-4 p-6">
      <p className="text-xs text-muted-foreground">
        {columns.length} columns · {totalRows.toLocaleString()} rows detected
      </p>
      <div className="overflow-auto rounded-lg border border-border scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent" style={{maxHeight: "400px"}}>
        <table className="border-collapse w-max min-w-full">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-muted border-r border-b border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground min-w-[40px] w-10">
                #
              </th>
              {columns.map((col) => (
                <th key={col} className="bg-muted border-r border-b border-border px-0 py-0 min-w-[130px] align-top last:border-r-0">
                  <div className="px-3 py-2">
                    <div className="text-xs font-medium text-foreground">{col}</div>
                  </div>
                  <div className="border-t border-border">
                    <Select value={mapping[col] || '__skip__'} onValueChange={(v) => setMapping((prev) => { const next = {...prev}; if (v === '__skip__') { delete next[col]; } else { next[col] = v; } return next; })}>
                      <SelectTrigger className={cn(
                        'h-7 w-full rounded-none border-0 text-[11px] focus:ring-0',
                        mapping[col] ? 'text-blue-700 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/30' : 'text-muted-foreground'
                      )}>
                        <SelectValue placeholder="— skip —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__skip__">— skip —</SelectItem>
                        {COLLAR_FIELDS.map((f) => {
                          const usedByOther = Object.entries(mapping).some(([k, v]) => v === f.key && k !== col);
                          return (
                            <SelectItem key={f.key} value={f.key} disabled={usedByOther}>
                              {f.label}{f.required ? ' *' : ''}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, rowIdx) => {
              const inactive = rowIdx + 1 < (parseInt(minRow) || 1);
              return (
                <tr key={rowIdx} className={inactive ? 'opacity-30' : ''}>
                  <td className="sticky left-0 z-10 bg-muted border-r border-b border-border px-3 py-1.5 text-[10px] text-muted-foreground last:border-b-0 font-mono">
                    {rowIdx + 1}
                  </td>
                  {columns.map((col, colIdx) => (
                    <td key={col} className="border-r border-b border-border px-3 py-1.5 text-[11px] font-mono last:border-r-0 whitespace-nowrap text-foreground">
                      {row[colIdx] ?? ''}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-muted-foreground">First data row</label>
          <Input type="number" min={1} value={minRow} onChange={(e) => setMinRow(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-muted-foreground">Last row <span className="text-muted-foreground/60">(optional)</span></label>
          <Input type="number" min={1} value={maxRow} onChange={(e) => setMaxRow(e.target.value)} placeholder={String(totalRows)} />
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );

  // ── Step: confirm ──────────────────────────────────────────────────────────

  const StepConfirm = (
    <div className="flex flex-col gap-3 p-6">
      <p className="text-xs text-muted-foreground">Review your mapping before uploading.</p>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full border-collapse text-sm">
          <tbody>
            {isExcel && (
              <tr className="border-b border-border">
                <td className="px-4 py-2.5 text-muted-foreground text-xs w-40">Sheet</td>
                <td className="px-4 py-2.5 font-medium text-foreground text-xs">{selectedSheet}</td>
              </tr>
            )}
            {COLLAR_FIELDS.map((f) => {
              const matched = Object.entries(mapping).find(([, v]) => v === f.key)?.[0];
              return (
                <tr key={f.key} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-2.5 text-muted-foreground text-xs w-40">
                    {f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs font-medium">
                    {matched
                      ? <span className="text-foreground">{matched}</span>
                      : <span className="text-muted-foreground/50">not mapped</span>
                    }
                  </td>
                </tr>
              );
            })}
            <tr className="border-b border-border last:border-b-0">
              <td className="px-4 py-2.5 text-muted-foreground text-xs">Rows</td>
              <td className="px-4 py-2.5 text-xs font-medium text-foreground">
                {minRow} → {maxRow || totalRows.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── Footer logic ───────────────────────────────────────────────────────────

  const nextDisabled =
    (step === 'file'    && !file) ||
    (step === 'sheet'   && !selectedSheet) ||
    (step === 'columns' && !allRequiredMapped) ||
    loadingSheets || loadingCols;

  const nextLabel =
    loadingSheets || loadingCols ? 'Loading…' :
    step === 'confirm' ? 'Upload' : 'Next →';

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`${step === 'confirm' ? 'sm:max-w-[520px] w-[92vw] sm:w-[520px]' : 'w-[92vw] sm:max-w-[60vw] sm:w-[60vw]'} gap-0 p-0 overflow-hidden resize-x`} onInteractOutside={(e) => e.preventDefault()}>
        <DialogTitle className="sr-only">Upload collars</DialogTitle>
        <DialogDescription className="sr-only">Import collar data from a CSV or Excel file.</DialogDescription>
        <Stepper steps={steps.filter((s) => s !== 'confirm')} current={step} allSteps={steps} />

        <div className="overflow-y-auto">
          {step === 'file'    && StepFile}
          {step === 'sheet'   && StepSheet}
          {step === 'columns' && StepColumns}
          {step === 'confirm' && StepConfirm}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-muted/40 min-w-0">
          <span className="text-xs text-muted-foreground truncate max-w-[180px]" title={file?.name ?? ''}>
            {file?.name ?? 'No file selected'}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={step === 'file' ? handleClose : handleBack}>
              {step === 'file' ? 'Cancel' : '← Back'}
            </Button>
            <Button
              size="sm"
              disabled={nextDisabled}
              onClick={step === 'confirm' ? handleConfirm : handleNext}
            >
              {nextLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}